import { ErrorMessage } from '@hookform/error-message';
import { Delete } from '@mui/icons-material';
import {
  Autocomplete,
  FormControl,
  GridLegacy as Grid,
  IconButton,
  TextField,
} from '@mui/material';
import { TextFieldProps } from '@mui/material/TextField/TextField';
import {
  DateRange,
  DateRangePicker,
  MultiInputDateRangeField,
} from '@mui/x-date-pickers-pro';
import moment, { Moment } from 'moment';
import * as React from 'react';
import { useCallback, useMemo } from 'react';
import {
  Controller,
  ControllerRenderProps,
  FieldArrayWithId,
  UseFieldArrayRemove,
  UseFormReturn,
  useController,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { EmployeeItemInfoData } from '~/components/GenericList/EmployeeItemInfo';
import { OrgUnitFormData } from '~/scenes/PortalViews/Views/PortalAltMgr/components/OrgUnitPopup';

type PropsType = {
  form: UseFormReturn<OrgUnitFormData>;
  alternateMgrs: EmployeeItemInfoData[];
  disabled?: boolean;
  commonTextFieldProps?: Partial<TextFieldProps>;
  item: FieldArrayWithId<OrgUnitFormData, 'alternateManagers'>;
  index: number;
  remove: UseFieldArrayRemove;
};

export const OrgUnitAltManagerRow = (props: PropsType) => {
  const {
    form,
    alternateMgrs,
    disabled,
    commonTextFieldProps,
    item,
    index,
    remove,
  } = props;

  const { t } = useTranslation();

  const { savedWithoutDates } = item;

  const {
    control,
    formState: { errors },
  } = form;

  const {
    field: { value: startValueString, onChange: onStartChange, ...startField },
  } = useController({
    name: `alternateManagers.${index}.startDate`,
    control: control,
  });

  const {
    field: { value: endValueString, onChange: onEndChange, ...endField },
  } = useController({
    name: `alternateManagers.${index}.endDate`,
    control: control,
  });

  const { values, isStartDateLocked } = useMemo(() => {
    const stringToDateOrNull = (value: string | null) =>
      value ? moment(value as string, 'YYYY-MM-DD') : null;

    const isStartDateEditable = (startDate: string | null) => {
      if (!startDate) return true;
      return moment(startDate).isSameOrAfter(moment().startOf('day'));
    };

    const values: DateRange<Moment> = [
      stringToDateOrNull(startValueString),
      stringToDateOrNull(endValueString),
    ];

    return {
      values,
      isStartDateLocked: !isStartDateEditable(startValueString),
    };
  }, [startValueString, endValueString]);

  const handleRangeChange = useCallback(
    (dateRange: DateRange<Moment>) => {
      const [start, end] = dateRange;
      const [startValue, endValue] = values;

      const dateToStringOrNull = (date: Moment | null) =>
        date ? moment(date).format('YYYY-MM-DD') : null;

      if (start !== startValue) {
        onStartChange(dateToStringOrNull(start));
      }
      if (end !== endValue) {
        onEndChange(dateToStringOrNull(end));
      }
    },
    [values, onStartChange, onEndChange],
  );

  const getTextFieldProps = (
    fieldProps: Omit<
      ControllerRenderProps<OrgUnitFormData>,
      'value' | 'onChange'
    >,
    label: string,
  ): TextFieldProps => {
    const hasFieldError = errors.hasOwnProperty(fieldProps.name);

    return {
      ...fieldProps,
      label,
      required: false,
      fullWidth: true,
      error: hasFieldError,
      helperText: hasFieldError && (
        <ErrorMessage errors={errors} name={fieldProps.name} />
      ),
      ...commonTextFieldProps,
    };
  };

  const additionalDisabledCondition = useCallback(
    (textFieldProps: TextFieldProps, disabledCondition: boolean) => {
      const { disabled, ...rest } = textFieldProps;
      return {
        ...rest,
        disabled: disabled || disabledCondition,
      };
    },
    [],
  );

  // Function to check if a manager is already added without dates
  const isManagerAddedWithoutDates = (managerId: string) => {
    return form
      .getValues('alternateManagers')
      .some(
        (mgr) =>
          mgr.manager?.employeeId === parseInt(managerId) &&
          !mgr.startDate &&
          !mgr.endDate,
      );
  };

  return (
    <Grid item xs={12}>
      <Grid
        container
        direction="row"
        spacing={2}
        key={`alt-manager-row-${index}`}
      >
        <Grid item xs={5}>
          <Controller
            control={control}
            name={`alternateManagers.${index}.manager`}
            render={({ field: { onChange, value } }) => {
              return (
                <FormControl fullWidth>
                  <Autocomplete
                    value={value}
                    options={alternateMgrs}
                    getOptionLabel={(manager) =>
                      `${manager.employee?.fEmpFirstName} ${manager.employee?.fEmpLastName}, ${manager.employee?.fEmpPersonNumber}`
                    }
                    onChange={(_, newValue) => {
                      onChange(newValue);
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option.employeeId === value.employeeId
                    }
                    disabled={disabled}
                    renderOption={(props, option) => (
                      <li
                        {...props}
                        style={{
                          pointerEvents: isManagerAddedWithoutDates(
                            option.employeeId.toString(),
                          )
                            ? 'none'
                            : 'auto',
                          color: isManagerAddedWithoutDates(
                            option.employeeId.toString(),
                          )
                            ? 'gray'
                            : 'inherit',
                        }}
                      >
                        {`${option.employee?.fEmpFirstName} ${option.employee?.fEmpLastName}, ${option.employee?.fEmpPersonNumber}`}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('laAltManager')}
                        {...commonTextFieldProps}
                      />
                    )}
                  />
                </FormControl>
              );
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <DateRangePicker
            minDate={moment().add(1, 'day')}
            value={values}
            onChange={handleRangeChange}
            disabled={disabled || savedWithoutDates}
            rangePosition={isStartDateLocked ? 'end' : undefined}
            slots={{
              field: (fieldProps) => (
                <MultiInputDateRangeField
                  {...fieldProps}
                  slotProps={{
                    root: {
                      sx: {
                        // Reduce spacing around separator
                        '& > :not(style) ~ :not(style)': {
                          marginLeft: '4px',
                        },
                      },
                    },
                    textField: ({ position }) => {
                      switch (position) {
                        case 'start':
                          return additionalDisabledCondition(
                            getTextFieldProps(startField, t('startDate')),
                            isStartDateLocked,
                          );
                        case 'end':
                          return getTextFieldProps(endField, t('endDate'));
                        default:
                          return {};
                      }
                    },
                  }}
                />
              ),
            }}
          />
        </Grid>
        <Grid item xs={1}>
          <IconButton
            size="small"
            onClick={() => remove(index)}
            disabled={disabled}
          >
            <Delete />
          </IconButton>
        </Grid>
      </Grid>
    </Grid>
  );
};
